<template>
  <div>
    <CModal
      :title="`${showDeleteTimeSlot ? toTitleCase('delete') : toTitleCase(mode)} timeslot`"
      :color="showDeleteTimeSlot ? 'danger' : 'success'"
      :show="true"
      @update:show="$emit('close')"
    >
      <CRow>
        <CCol>
          <label>Weekdays</label>
          <CMultiSelect
            :v-model="selectedWeekDaysAdd"
            :disabled="showDeleteTimeSlot"
            :multiple="true"
            :selection="true"
            optionsEmptyPlaceholder="No options placeholder"
            selectionType="text"
            :options="options"
            :selected.sync="selectedWeekDaysAdd"
            @update="appendValueAdd($event)"
          />
        </CCol>
      </CRow>
      <CRow style="margin-top: 1rem;">
        <CCol col="6">
          <label>Start time</label>
          <input
            :value="formattedStartTime"
            class="form-control"
            :disabled="showDeleteTimeSlot"
            type="time"
            @keyup="saveStartTimeslotTime($event)"
            @keypress=""
            @keydown="saveStartTimeslotTime($event)"
            @change="saveStartTimeslotTime($event)"
          />
        </CCol>
        <CCol col="6">
          <label>End time</label>
          <input
            :value="formattedEndTime"
            class="form-control"
            :disabled="showDeleteTimeSlot"
            type="time"
            @keyup="saveEndTimeslotTime($event)"
            @keypress=""
            @keydown="saveEndTimeslotTime($event)"
            @change="saveEndTimeslotTime($event)"
          />
        </CCol>
      </CRow>
      <CRow style="margin-top: 2rem; margin-bottom: 1rem; margin-left:0.6rem">
        <p style="display: flex; gap: 0.5rem;">
          <CIcon name="cilFactory"></CIcon>
          <span style="font-weight: bold;">Additional options</span>
        </p>
      </CRow>
      <CRow>
        <CCol col="6">
          <CInput :disabled="showDeleteTimeSlot" label="Slot length (minutes)" v-model.number="addSlotLength" type="number" min="0" />
        </CCol>
        <CCol col="6">
          <CInput :disabled="showDeleteTimeSlot" label="Repeat every (minutes)" v-model.number="addRepeatLength" type="number" min="0" />
        </CCol>
      </CRow>
      <template #footer>
        <CButton v-if="!showDeleteTimeSlot" color="secondary" @click="$emit('close')">Cancel</CButton>
        <CButton v-if="mode === 'edit' && !showDeleteTimeSlot" color="danger" @click="showDeleteTimeSlot = true">
          <CIcon name="cil-trash" /> Delete
        </CButton>
        <CButton v-if="!showDeleteTimeSlot" color="success" @click="saveChanges()">OK</CButton>
        <CRow v-if="showDeleteTimeSlot" style="padding: 0.75rem;">
        <p style="font-weight: bold;"> Are you sure that you want to delete this timeslot?</p>
        <div style="display: flex; justify-content: flex-end; gap: 0.5rem; width: 100%;">
          <CButton color="secondary" @click="closeModalDelete(false)">Cancel</CButton>
          <CButton color="danger" @click="closeModalDelete(true)">
            <CIcon name="cil-trash" /> Delete
          </CButton>
        </div>
      </CRow>
      </template>
    </CModal>
  </div>
</template>

<script setup>
// @ts-check

/**
 * @template T
 * @typedef {import('vue').Ref<T>} Ref
 */
/**
 * @typedef {import('@/types').TimeSlot} TimeSlot
 * @typedef {import('@/types').TimeSlotEditPayload} TimeSlotEditPayload
 */
import { computed, ref, reactive } from 'vue';
import { toTitleCase } from '@/utils/utils'

const emit = defineEmits(['close']);
const props = defineProps({
  mode: {
    /** @type {import('vue').PropType<'add' | 'edit' | 'delete'>}  */
    // @ts-expect-error
    type: String, 
    required: true,
  },
  initialTimeSlotData: {
    /** @type {import('vue').PropType<TimeSlot | null>}  */
    type: [Object, null]
  }
});

const selectedWeekDaysAdd = ref(props.initialTimeSlotData?.days ?? []);
const options = [
  { value: 1, text: 'Monday' },
  { value: 2, text: 'Tuesday' },
  { value: 3, text: 'Wednesday' },
  { value: 4, text: 'Thursday' },
  { value: 5, text: 'Friday' },
  { value: 6, text: 'Saturday' },
  { value: 7, text: 'Sunday' }
];

const createPayload = () => {
  /** @type {TimeSlotEditPayload} */
  const payload = {
    selectedWeekDaysAdd: selectedWeekDaysAdd.value,
    endOfTimeslot: endOfTimeslot,
    startOfTimeslot: startOfTimeslot,
    addRepeatLength: (addRepeatLength.value === 0 || addRepeatLength.value === '') ? null : Number(addRepeatLength.value),
    addSlotLength: (addSlotLength.value === 0 || addSlotLength.value === '') ? null : Number(addSlotLength.value)
  }
  if (['edit', 'delete'].includes(props.mode)) {
    payload.timeSlotId = props.initialTimeSlotData.id;
  }
  return payload;
};

const showDeleteTimeSlot = ref(props.mode === 'delete');
const closeModalDelete = async (accept) => {
  if (accept) {
    const payload = createPayload();
    emit('close', { type: 'delete', payload });
  }
  showDeleteTimeSlot.value = false;
  if (props.mode === 'delete') {
    emit('close');
  }
};

const appendValueAdd = (event) => {
  selectedWeekDaysAdd.value = event.sort();
};

/** @type {Ref<number | string>} */
  const addSlotLength = ref(props.initialTimeSlotData?.slotLength);
  /** @type {Ref<number | string>} */
const addRepeatLength = ref(props.initialTimeSlotData?.repeatLength);

const startOfTimeslot = reactive({
  hours: props.initialTimeSlotData?.start.hours,
  minutes: props.initialTimeSlotData?.start.minutes
});
const endOfTimeslot = reactive({
  hours: props.initialTimeSlotData?.end.hours,
  minutes: props.initialTimeSlotData?.end.minutes
});
const saveStartTimeslotTime = (event) => {
  const [hoursStr, minutesStr] = event.target.value.split(':');
  Object.assign(startOfTimeslot, { hours: Number(hoursStr), minutes: Number(minutesStr) });
};
const formattedStartTime = computed(() => {
  if (startOfTimeslot.hours != null && startOfTimeslot.minutes != null) {
    return `${startOfTimeslot.hours.toString().padStart(2, '0')}:${startOfTimeslot.minutes.toString().padStart(2, '0')}`;
  }
  return '';
});

const formattedEndTime = computed(() => {
  if (endOfTimeslot.hours != null && endOfTimeslot.minutes != null) {
    return `${endOfTimeslot.hours.toString().padStart(2, '0')}:${endOfTimeslot.minutes.toString().padStart(2, '0')}`;
  }
  return '';
});

const saveEndTimeslotTime = (event) => {
  const [hours, minutes] = event.target.value.split(':');
  Object.assign(endOfTimeslot, { hours, minutes });
};

const saveChanges = async () => {
  if (!['add', 'edit'].includes(props.mode)) {
    return;
  }
  const payload = createPayload();
  emit('close', { type: props.mode, payload: payload });
};
</script>